import { useCallback, useEffect, useRef } from 'react';
import { Block } from '../types';
import { BlockService, BlockCalculationService } from '../services';
import { useBlockState } from './useBlockState';
import { useBlockActions } from './useBlockActions';
import { useBlockSelection } from './useBlockSelection';
import { useBlockDragDrop } from './useBlockDragDrop';

export const useBlockEditor = (initialBlocks: Block[] = []) => {
  const {
    state,
    updateBlock,
    addBlock,
    removeBlock,
    getBlocksInOrder,
    syncWithExternalBlocks,
    reorderBlocks: stateReorderBlocks
  } = useBlockState(initialBlocks);

  const actions = useBlockActions({
    state,
    updateBlock,
    addBlock,
    removeBlock
  });

  const selection = useBlockSelection();
  const dragDrop = useBlockDragDrop();

  // Référence pour éviter les mises à jour en boucle
  const lastBlocksRef = useRef<string>('');

  // Synchroniser avec les blocs externes
  useEffect(() => {
    if (initialBlocks.length > 0) {
      const blocksKey = initialBlocks.map(b => `${b.id}-${b.content.intitule ? 'prestation' : 'section'}`).join(',');
      
      if (lastBlocksRef.current !== blocksKey) {
        lastBlocksRef.current = blocksKey;
        syncWithExternalBlocks(initialBlocks);
      }
    }
  }, [initialBlocks, syncWithExternalBlocks]);

  // Sélection et édition
  const selectBlock = useCallback((id: string) => {
    selection.selectBlock(id);
  }, [selection]);

  const editBlock = useCallback((id: string) => {
    updateBlock(id, {});
    // Ici on pourrait ajouter une logique pour marquer le bloc comme en édition
  }, [updateBlock]);

  const stopEditing = useCallback(() => {
    // Logique pour arrêter l'édition
  }, []);

  // Réorganiser les blocs (pour le drag & drop) — délègue à l'action atomique de useBlockState
  const reorderBlocks = useCallback((
    blockIds: string[],
    targetIndex: number,
    targetParentId?: string,
    _targetLevel?: number // ignoré : le niveau est recalculé depuis le parent
  ) => {
    const currentBlocksInOrder = getBlocksInOrder();
    stateReorderBlocks(blockIds, targetIndex, targetParentId, currentBlocksInOrder);
  }, [getBlocksInOrder, stateReorderBlocks]);

  // Calculer le total du chiffrage
  const calculateTotal = useCallback(() => {
    const blocks = getBlocksInOrder();
    return BlockCalculationService.calculateTotal(blocks);
  }, [getBlocksInOrder]);

  // Obtenir les enfants d'un bloc
  const getChildren = useCallback((parentId: string): Block[] => {
    return BlockService.getChildren(state.blocks, parentId);
  }, [state.blocks]);

  // Obtenir le parent d'un bloc
  const getParent = useCallback((parentId: string): Block | null => {
    return BlockService.getParent(state.blocks, parentId);
  }, [state.blocks]);

  return {
    state,
    actions,
    selection,
    dragDrop,
    selectBlock,
    toggleSelection: selection.toggleSelection,
    selectRange: selection.selectRange,
    selectAllBlocks: selection.selectAllBlocks,
    toggleSelectAll: selection.toggleSelectAll,
    clearSelection: selection.clearSelection,
    editBlock,
    stopEditing,
    getBlocksInOrder,
    reorderBlocks,
    calculateTotal,
    getChildren,
    getParent,
    syncLevels: actions.syncLevels,
    syncWithExternalBlocks,
  };
};